import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { DashboardEditorProps } from "components/ComponentTypeTypes";
import { aggregationOptions } from "components/dashboard/product-template-bar-chart/product-template-bar-chart-editor/ProductTemplateBarChartTypes";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import { InputSwitch } from "primereact/inputswitch";
import { InputNumber } from "primereact/inputnumber";
import React, { FC, useEffect, useState } from "react";
import { T } from "services/lang/LanguageService";
import { Divider } from "primereact/divider";
import LoadConfigInputs, { DefaultDatasetLoadingConf } from "components/dashboard/charts/LoadConfigInputs";
import { DatasetLoadingConf } from "components/dashboard/charts/interfaces";
import {
  ChartType,
  chartTypeOptions,
  ComparisonPeriod,
  comparisonPeriodOptions,
  DateRangeSelectType,
  dateRangeSelectTypeOptions,
  PeriodicDataChartProps,
} from "../PeriodicDataChartTypes";
import MarginEditor from "./MarginEditor";

const PeriodicDataChartEditor: FC<DashboardEditorProps<PeriodicDataChartProps>> = ({
  config,
  onConfigChange,
}) => {
  const [currentConfig, setCurrentConfig] = useState<PeriodicDataChartProps>({
    ...config,
    loadConf: config.loadConf ?? DefaultDatasetLoadingConf,
  });

  useEffect(() => {
    onConfigChange(currentConfig);
  }, [currentConfig]);

  const setToDate = (e: Date, type: DateRangeSelectType) => {
    const year = e.getFullYear();
    if (type === DateRangeSelectType.YEAR) {
      const endOfYear = new Date(year, 11, 31, 23, 59, 59);
      return setCurrentConfig({ ...currentConfig, dateTo: endOfYear.toISOString() });
    }

    const selected = new Date(e);
    const month = selected.getMonth();
    const lastDay = new Date(year, month + 1, 0);
    lastDay.setHours(23, 59, 0, 0);
    return setCurrentConfig({ ...currentConfig, dateTo: lastDay.toISOString() });
  };

  return (
    <div>
      <div className="flex flex-row gap-3">
        <div className="flex flex-column gap-2 w-6">
          <label htmlFor="valueDataset">{T("word.dataset")}</label>
          <DatasetDropdown
            value={currentConfig.valueDataset}
            onChange={val => setCurrentConfig({ ...currentConfig, valueDataset: val ?? "" })}
          />
        </div>
        <div className="flex flex-column gap-2 w-3">
          <label htmlFor="title">{T("word.title")}</label>
          <InputText
            id="title"
            value={currentConfig.title}
            onChange={e => setCurrentConfig({ ...currentConfig, title: e.target.value })}
          />
        </div>
        <div className="flex flex-column gap-2 w-3">
          <label htmlFor="subtitle">{T("word.subtitle")}</label>
          <InputText
            id="subtitle"
            value={currentConfig.subtitle}
            onChange={e => setCurrentConfig({ ...currentConfig, subtitle: e.target.value })}
          />
        </div>
      </div>
      <Divider />
      <div className="flex flex-row gap-3">
        <div className="gap-2 flex flex-column justify-content-center">
          <label htmlFor="chartType">{T("label.chart-type")}</label>
          <Dropdown
            id="chartType"
            value={currentConfig.chartType ?? ChartType.BAR}
            options={chartTypeOptions}
            placeholder={T("word.select-one")}
            className="p-inputtext-sm w-14rem"
            onChange={e => setCurrentConfig({ ...currentConfig, chartType: e.value })}
          />
        </div>
        <div className="gap-2 flex flex-column justify-content-center">
          <label htmlFor="aggregationType">{T("label.aggregation-type")}</label>
          <Dropdown
            id="aggregationType"
            value={currentConfig.aggregationType ?? "SUM"}
            options={aggregationOptions}
            placeholder={T("word.select-one")}
            className="p-inputtext-sm w-14rem"
            onChange={e => setCurrentConfig({ ...currentConfig, aggregationType: e.value })}
          />
        </div>
        <div className="gap-2 flex flex-column justify-content-center">
          <label htmlFor="dateRangeSelectType">{T("label.date-range-type")}</label>
          <Dropdown
            id="dateRangeSelectType"
            value={currentConfig.dateRangeSelectType ?? DateRangeSelectType.CUSTOM}
            options={dateRangeSelectTypeOptions}
            placeholder={T("word.select-one")}
            className="p-inputtext-sm w-14rem"
            onChange={e => setCurrentConfig({ ...currentConfig, dateRangeSelectType: e.value })}
          />
        </div>
        <div className="gap-2 flex flex-column justify-content-center">
          <label htmlFor="comparisonPeriod">{T("label.aggregation-period-type")}</label>
          <Dropdown
            id="comparisonPeriod"
            value={currentConfig.comparisonPeriod ?? ComparisonPeriod.MONTHLY_YEARLY}
            options={comparisonPeriodOptions}
            placeholder={T("word.select-one")}
            className="p-inputtext-sm w-14rem"
            onChange={e => setCurrentConfig({ ...currentConfig, comparisonPeriod: e.value })}
          />
        </div>
      </div>
      <Divider />
      <div className="flex flex-row justify-content-between">
        {currentConfig.dateRangeSelectType === DateRangeSelectType.LOAD_CONF && (
          <div className="flex flex-row justify-content-between">
            <LoadConfigInputs
              loadConf={currentConfig.loadConf}
              handleLoadConfChange={(updatedLoadConf: DatasetLoadingConf) => {
                setCurrentConfig({ ...currentConfig, loadConf: updatedLoadConf });
              }}
            />
          </div>
        )}
        {(currentConfig.dateRangeSelectType === DateRangeSelectType.CUSTOM ||
          currentConfig.dateRangeSelectType === undefined) && (
          <div className="flex flex-row gap-2">
            <div className="flex flex-column gap-2">
              <label htmlFor="from">{T("word.from")}</label>
              <CalendarWrapper
                id="from"
                value={currentConfig.dateFrom ? new Date(currentConfig.dateFrom) : new Date()}
                onChange={e => {
                  setCurrentConfig({ ...currentConfig, dateFrom: e.toString() });
                }}
              />
            </div>
            <div className="flex flex-column gap-2">
              <label htmlFor="to">{T("word.to")}</label>
              <CalendarWrapper
                id="to"
                value={currentConfig.dateTo ? new Date(currentConfig.dateTo) : new Date()}
                onChange={e => {
                  setCurrentConfig({ ...currentConfig, dateTo: e.toString() });
                }}
              />
            </div>
          </div>
        )}
        {currentConfig.dateRangeSelectType === DateRangeSelectType.MONTH && (
          <div className="flex flex-row gap-2">
            <div className="flex flex-column gap-2">
              <label htmlFor="from">{T("word.from")}</label>
              <CalendarWrapper
                id="from"
                value={currentConfig.dateFrom ? new Date(currentConfig.dateFrom) : new Date()}
                view="month"
                dateFormat="mm/yy"
                showTime={false}
                onChange={e => {
                  setCurrentConfig({ ...currentConfig, dateFrom: e.toString() });
                }}
              />
            </div>
            <div className="flex flex-column gap-2">
              <label htmlFor="to">{T("word.to")}</label>
              <CalendarWrapper
                id="to"
                value={currentConfig.dateTo ? new Date(currentConfig.dateTo) : new Date()}
                view="month"
                dateFormat="mm/yy"
                showTime={false}
                onChange={e => setToDate(e, DateRangeSelectType.MONTH)}
              />
            </div>
          </div>
        )}
        {currentConfig.dateRangeSelectType === DateRangeSelectType.YEAR && (
          <div className="flex flex-row gap-2">
            <div className="flex flex-column gap-2">
              <label htmlFor="from">{T("word.from")}</label>
              <CalendarWrapper
                id="from"
                value={currentConfig.dateFrom ? new Date(currentConfig.dateFrom) : new Date()}
                view="year"
                showTime={false}
                dateFormat="yy"
                onChange={e => {
                  setCurrentConfig({ ...currentConfig, dateFrom: e.toString() });
                }}
              />
            </div>
            <div className="flex flex-column gap-2">
              <label htmlFor="to">{T("word.to")}</label>
              <CalendarWrapper
                id="to"
                value={currentConfig.dateTo ? new Date(currentConfig.dateTo) : new Date()}
                showTime={false}
                view="year"
                dateFormat="yy"
                onChange={e => setToDate(e, DateRangeSelectType.YEAR)}
              />
            </div>
          </div>
        )}
        <div className="flex flex-column gap-3 w-2">
          <label htmlFor="updateFrequency">{T("load-conf.update-frequency")}</label>
          <div className="p-inputgroup w-full">
            <InputNumber
              id="updateFrequency"
              value={config.updateFrequency as number}
              onValueChange={e => onConfigChange({ ...config, updateFrequency: e.value ?? 0 })}
            />
            <span className="p-inputgroup-addon">s</span>
          </div>
        </div>
      </div>
      <Divider />
      <div className="flex flex-row justify-content-between">
        {currentConfig.comparisonPeriod === ComparisonPeriod.WEEKLY_YEARLY ? (
          <div className="flex flex-row gap-2">
            <div className="flex flex-column gap-2">
              <label htmlFor="useDataUpToLastFinishedWeek">
                {T("load-conf.use-data-up-to-last-finished-week")}
              </label>
              <InputSwitch
                id="useDataUpToLastFinishedWeek"
                checked={config.useDataUpToLastFinishedWeek ?? false}
                onChange={e => onConfigChange({ ...config, useDataUpToLastFinishedWeek: e.value })}
              />
            </div>
            <div className="flex flex-column gap-2">
              <label htmlFor="hideFutureValues">{T("load-conf.hide-future-values")}</label>
              <InputSwitch
                id="hideFutureValues"
                checked={config.hideFutureValues ?? false}
                onChange={e => onConfigChange({ ...config, hideFutureValues: e.value })}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-column gap-2">
            <label htmlFor="useDataUpToLastFinishedMonth">
              {T("load-conf.use-data-up-to-last-finished-month")}
            </label>
            <InputSwitch
              id="useDataUpToLastFinishedMonth"
              checked={config.useDataUpToLastFinishedMonth ?? false}
              onChange={e => onConfigChange({ ...config, useDataUpToLastFinishedMonth: e.value })}
            />
          </div>
        )}
        <div className="flex flex-column gap-2">
          <label htmlFor="hideLegend">{T("action.hide-legend")}</label>
          <InputSwitch
            id="hideLegend"
            checked={config.hideLegend ?? false}
            onChange={e => onConfigChange({ ...config, hideLegend: e.value })}
          />
        </div>
        {currentConfig.comparisonPeriod === ComparisonPeriod.WEEKLY_YEARLY && (
          <div className="flex flex-row gap-2">
            <div className="flex flex-column gap-2">
              <label htmlFor="weeksRangeStartOffset">{`${T("label.range-start-offset")} (${T("time.weeks")})`}</label>
              <InputNumber
                id="weeksRangeStartOffset"
                value={config.weeksRangeStartOffset as number}
                onValueChange={e => onConfigChange({ ...config, weeksRangeStartOffset: e.value ?? 0 })}
              />
            </div>
            <div className="flex flex-column gap-2">
              <label htmlFor="weeksRangeEndOffset">{`${T("label.range-end-offset")} (${T("time.weeks")})`}</label>
              <InputNumber
                id="weeksRangeEndOffset"
                value={config.weeksRangeEndOffset as number}
                onValueChange={e => onConfigChange({ ...config, weeksRangeEndOffset: e.value ?? 0 })}
              />
            </div>
          </div>
        )}
      </div>
      <Divider />
      <MarginEditor currentConfig={currentConfig} setCurrentConfig={setCurrentConfig} />
    </div>
  );
};

export default PeriodicDataChartEditor;
